import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Form, Button, Alert, Col, Row, Modal, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';

const AddOffer = ({ show, handleClose }) => {
    const [storeBrands, setStoreBrands] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        coupon_code: '',
        discount: 0,
        start_date: moment().format('YYYY-MM-DD'),
        end_date: '',
        status: 'ACTIVE',
        is_verified: false,
        is_custom: true,
        store_brand_id: '',
        store_id: '',
        extra_detail: '',
    });
    const [expandDescription, setExpandDescription] = useState(false);
    const [showMoreOptions, setShowMoreOptions] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // State for favorite stores
    const [favoriteStores, setFavoriteStores] = useState([]);
    const [storeDropdownEnabled, setStoreDropdownEnabled] = useState(false);

    // Retrieve token and user info from localStorage
    const token = localStorage.getItem('token');

    const fetchStoreBrands = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-store-brands/`, {
                headers: { Authorization: `Token ${token}` },
            });
            setStoreBrands(response.data.results);
        } catch (error) {
            console.error('Error fetching store brands:', error);
        }
    }, [token]);

    useEffect(() => {
        // Fetch store brands
        fetchStoreBrands();
    }, [fetchStoreBrands]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });

        // Handle store dropdown
        if (e.target.name === 'store_brand_id' && e.target.value) {
            fetchFavoriteStores(e.target.value);
        }
    };

    const fetchFavoriteStores = async (brandId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-stores/?brand_id=${brandId}`, {
                headers: { Authorization: `Token ${token}` },
            });
            setFavoriteStores(response.data.results);
            setStoreDropdownEnabled(true); // Enable the store dropdown
        } catch (error) {
            console.error('Error fetching favorite stores:', error);
            setFavoriteStores([]);
            setStoreDropdownEnabled(false); // Disable the store dropdown
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Ensure a store is selected
        if (!formData.store_brand_id) {
            setErrorMessage('Please select a store brand.');
            return; // Stop the form submission
        }

        // Ensure start_date is selected and end_date is not before start_date
        if (!formData.start_date) {
            setErrorMessage('Please select a start date.');
            return; // Stop the form submission
        }
        if (formData.end_date && moment(formData.end_date).isBefore(formData.start_date)) {
            setErrorMessage('End date cannot be before start date.');
            return; // Stop the form submission
        }

        // Construct the payload correctly
        let submissionData = {
            name: formData.name,
            description: formData.description,
            coupon_code: formData.coupon_code,
            discount: formData.discount,
            start_date: formData.start_date,
            end_date: formData.end_date || undefined,
            status: formData.status,
            is_custom: formData.is_custom,
            store_brand_id: formData.store_brand_id,
            store_details: [{
                store_id: formData.store_id,
                extra_detail: formData.extra_detail,
            }]
        };
        console.log("Offer Add Form Data", submissionData);
        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-offers/`, submissionData, {
                headers: { Authorization: `Token ${token}` },
            });
            console.log("Submitted form data", submissionData);
            console.log("Offer Add Response", response); // Log response for debugging
            toast.success('Offer added successfully!');
            setFormData({
                name: '',
                description: '',
                coupon_code: '',
                discount: 0,
                start_date: '',
                end_date: '',
                status: 'ACTIVE',
                is_verified: false,
                is_custom: true,
                store_brand_id: '',
                store_id: '',
                extra_detail: '',
            });
            handleClose();

        } catch (error) {
            console.error("Failed to add offer", error.response ? error.response.data : error); // Log error for debugging
            toast.error('Failed to add offer. Please try again.');
        }
    };


    const handleToggle = () => {
        setFormData({ ...formData, is_custom: !formData.is_custom });
    };

    return (
        <Container>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Offer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Store Brand
                                        <OverlayTrigger
                                            placement="top"
                                            delay={{ show: 250, hide: 400 }}
                                            overlay={
                                                <Tooltip id="tooltip-top">
                                                    Store brand needed
                                                </Tooltip>
                                            }
                                        >
                                            <span style={{ color: 'red', marginLeft: '5px' }}>*</span>
                                        </OverlayTrigger>
                                    </Form.Label>
                                    <Form.Select name="store_brand_id" value={formData.store_brand_id} onChange={handleChange}>
                                        <option value="">Select Store Brand</option>
                                        {storeBrands.map(brand => (
                                            <option key={brand.id} value={brand.id}>{brand.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Store</Form.Label>
                                    <Form.Select name="store_id" value={formData.store_id} onChange={handleChange} disabled={!storeDropdownEnabled}>
                                        <option value="">Select Favorite Store</option>
                                        {favoriteStores.map(store => (
                                            <option key={store.store_details.id} value={store.store_details.id}>{store.store_details.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Store Extra Detail</Form.Label>
                                <Form.Control type="text" name="extra_detail" value={formData.extra_detail} onChange={handleChange} placeholder="Extra details about the store" />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Name</Form.Label>
                                <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Offer Name (Optional)" />
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="isCustomSwitch"
                                    label="Share this offer with the community"
                                    checked={!formData.is_custom}
                                    onChange={handleToggle}
                                />
                            </Form.Group>
                        </Row>
                        <Row>
                            {/* Button to show more options */}
                            <Button variant="secondary" onClick={() => setShowMoreOptions(!showMoreOptions)} className="mb-3" style={{ width: '180px', height: '40px' }}>
                                {showMoreOptions ? 'Less Options' : 'More Options'}
                            </Button>

                            {showMoreOptions && (
                                <div style={{ backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', marginBottom: '20px' }}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control as="textarea" rows={expandDescription ? 5 : 1} name="description" value={formData.description} onChange={handleChange} placeholder="Offer Description" onFocus={() => setExpandDescription(true)} onBlur={() => setExpandDescription(false)} />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Coupon Code</Form.Label>
                                        <Form.Control type="text" name="coupon_code" value={formData.coupon_code} onChange={handleChange} placeholder="Coupon Code" />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Discount (%)</Form.Label>
                                        <Form.Control type="number" name="discount" value={formData.discount} onChange={handleChange} placeholder="Discount in percentage" />
                                    </Form.Group>
                                </div>
                            )}
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control type="date" name="start_date" value={formData.start_date} onChange={handleChange} required />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>End Date (Optional)</Form.Label>
                                    <Form.Control type="date" name="end_date" value={formData.end_date} onChange={handleChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleSubmit}>
                        Add Offer
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AddOffer;
